/**
 * Facility-code catalog for the station-page Facilities section
 * (TASK-699, Figma 8597:41774). schema.json facility codes are dirty —
 * the same amenity appears as WIFI / WI-FI / WiFi / WI_FI / wifi and
 * WAITING_ROOM / waitingRoom — so every comparison goes through
 * `normalizeFacilityCode` (uppercase, alphanumerics only).
 */

/**
 * The fixed set of facilities the design shows, in display order.
 * `labelKey` resolves in the `station-page` i18n namespace.
 */
export const FACILITY_CATALOG = [
  { key: 'BRIDGE', labelKey: 'facility_bridge' },
  { key: 'WIFI', labelKey: 'facility_wifi' },
  { key: 'TOILETS', labelKey: 'facility_toilets' },
  { key: 'PAYPHONES', labelKey: 'facility_payphones' },
  { key: 'HELPPOINTS', labelKey: 'facility_help_points' },
  { key: 'ESCALATORS', labelKey: 'facility_escalators' },
  { key: 'WAITINGROOM', labelKey: 'facility_waiting_room' },
  { key: 'LIFTS', labelKey: 'facility_lifts' },
] as const

export type FacilityKey = (typeof FACILITY_CATALOG)[number]['key']

export interface AccessibilityFacts {
  stepFree: boolean
  escalators: boolean
  lifts: boolean
  accessibleToilet: boolean
}

/** Folds the schema's spelling variants into one canonical key. */
export function normalizeFacilityCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/** Availability of every catalog facility given a station's raw codes. */
export function facilityAvailability(rawCodes: readonly string[]): Record<FacilityKey, boolean> {
  const normalized = new Set(rawCodes.map(normalizeFacilityCode))
  const availability = {} as Record<FacilityKey, boolean>
  for (const { key } of FACILITY_CATALOG) {
    availability[key] = normalized.has(key)
  }
  return availability
}

const STEP_FREE_CODES = new Set([
  'STEPFREEFROMSTREET',
  'STEPFREEACCESS',
  'STEPFREE',
  'WHEELCHAIRACCESS',
])

const ACCESSIBLE_TOILET_CODES = new Set(['ACCESSIBLETOILET', 'NKSRADARTOILETS'])

/** The Accessibility column facts derived from a station's raw codes. */
export function accessibilityFacts(rawCodes: readonly string[]): AccessibilityFacts {
  const normalized = new Set(rawCodes.map(normalizeFacilityCode))
  return {
    stepFree: [...STEP_FREE_CODES].some((code) => normalized.has(code)),
    escalators: normalized.has('ESCALATORS'),
    lifts: normalized.has('LIFTS'),
    accessibleToilet: [...ACCESSIBLE_TOILET_CODES].some((code) => normalized.has(code)),
  }
}
